import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import {AutomationExecution, ExecutionStatus, NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {AutomationService} from "crqlar-react-common/dist/services/automation/AutomationService";
import {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {ElementChip} from "../../../common/uiElements/base/ElementChip";
import {ExecutionStatusElement} from "../../../common/uiElements/ExecutionStatusElement";
import {fanOutScope, isTerminal, parseStepResult, queuedChildren} from "../utils/executionSteps";
import {
    contactBlockText,
    fanOutMemberName,
    formatDate,
    formatDuration,
    formatEnumValue,
    formatStepResult,
    isTestRunExecution,
    stoppedAtText,
    surveyReportStepText,
    teamEmailStepText,
    triggerEntityLabel,
} from "./executionFormat.util";
import {loadMemberRuns} from "./memberRuns";

const EXECUTION_POLL_INTERVAL_MS = 4000;

interface ExecutionDetailsDialogProps {
    open: boolean;
    executionId: string;
    execution?: AutomationExecution;
    flowName?: string;
    onClose: () => void;
    /** Opens another run in this dialog's place — a fan-out child, or the parent a child came from. */
    onOpenExecution: (executionId: string) => void;
}

function JsonBlock({value, maxHeight = 300}: {value: string; maxHeight?: number}) {
    return (
        <Box
            component="pre"
            sx={{
                m: 0,
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                overflow: 'auto',
                maxHeight,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
            }}
        >
            {value}
        </Box>
    );
}

// A fan-out parent reads Completed once its member runs are queued, so a member's failure only shows in this row.
function ChildRunRow({execution, onOpen}: {execution: AutomationExecution; onOpen: (id: string) => void}) {
    const {t} = useTranslation();
    const member = fanOutMemberName(execution) || execution.triggerEntityId;
    const stoppedAt = stoppedAtText(execution, t);

    return (
        <Paper
            variant={'outlined'}
            sx={{p: 1.5, cursor: 'pointer'}}
            onClick={() => execution.id && onOpen(execution.id)}
        >
            <Stack direction={'row'} alignItems={'center'} gap={1.5} flexWrap={'wrap'}>
                <ExecutionStatusElement status={execution.status}/>

                {!!member && (
                    <Typography variant={'body2'} fontWeight={600} noWrap sx={{minWidth: 0}}>
                        {member}
                    </Typography>
                )}

                <Typography variant={'body2'} color={'text.secondary'} noWrap>
                    {formatDate(execution.startedAt)}
                    {' · '}
                    {formatDuration(execution.startedAt, execution.completedAt)}
                </Typography>

                {!!stoppedAt && (
                    <Typography variant={'body2'} color={'text.secondary'} noWrap sx={{minWidth: 0}}>
                        {stoppedAt}
                    </Typography>
                )}

                {!!execution.errorMessage && (
                    <Typography variant={'body2'} color={'error'} noWrap sx={{minWidth: 0, flex: 1}}>
                        {execution.errorMessage}
                    </Typography>
                )}

                <Button size={'small'} sx={{ml: 'auto', flexShrink: 0}}>
                    {t('Open run')}
                </Button>
            </Stack>
        </Paper>
    );
}

export function ExecutionDetailsDialog({
    open,
    executionId,
    execution: rowExecution,
    flowName,
    onClose,
    onOpenExecution,
}: ExecutionDetailsDialogProps) {
    const {t} = useTranslation();

    const executionQuery = useQuery(
        ['automation-execution', executionId],
        () => AutomationService.getExecution(executionId),
        {
            enabled: open && !!executionId,
            refetchOnWindowFocus: false,
            refetchInterval: execution => (
                execution && !isTerminal(execution)
                    ? EXECUTION_POLL_INTERVAL_MS
                    : false
            ),
        }
    );
    const execution = executionQuery.data ?? rowExecution;
    const steps = execution?.executionSteps ?? [];
    const contextEntries = Object.keys(execution?.context ?? {});
    const testRun = isTestRunExecution(execution);

    // A member run can fan out again (Audience → Team members); only its own steps count.
    const expectedChildren = execution ? queuedChildren(execution) : 0;
    const scope = execution ? fanOutScope(execution) : null;

    const childrenQuery = useQuery(
        ['automation-execution-children', executionId],
        () => loadMemberRuns(executionId),
        {
            enabled: open && expectedChildren > 0,
            refetchOnWindowFocus: false,
            // Keep looking while a member run is still going or not every queued one has appeared yet.
            refetchInterval: runs => {
                const children = (runs ?? []).filter(child => child.parentExecutionId === executionId);
                const settled = children.length >= expectedChildren && children.every(isTerminal);
                return settled ? false : EXECUTION_POLL_INTERVAL_MS;
            },
        }
    );
    const children = (childrenQuery.data ?? []).filter(child => child.parentExecutionId === executionId);
    const failedChildren = children.filter(child => child.status === ExecutionStatus.FAILED).length;
    const finishedChildren = children.filter(isTerminal).length;
    const childrenSummary = failedChildren
        ? t('Member runs failed summary', {failed: failedChildren, total: expectedChildren})
        : finishedChildren >= expectedChildren
            ? t('Member runs done summary', {total: expectedChildren})
            : t('Member runs pending summary', {done: finishedChildren, total: expectedChildren});

    const metaRow = (label: string, value?: ReactNode) => (
        value
            ? (
                <Stack gap={0.25} sx={{minWidth: 140}}>
                    <Typography variant={'body2'} color={'text.secondary'}>{label}</Typography>
                    <Typography variant={'body2'} component={'div'}>{value}</Typography>
                </Stack>
            )
            : null
    );

    const parentLink = execution?.parentExecutionId
        ? (
            <Link
                component={'button'}
                variant={'body2'}
                onClick={() => onOpenExecution(execution.parentExecutionId!)}
                sx={{fontFamily: 'inherit'}}
            >
                {execution.parentExecutionId}
            </Link>
        )
        : undefined;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={'md'}
            fullWidth
            PaperProps={{sx: {height: '90vh', maxHeight: '90vh'}}}
        >
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                    <Stack direction={'row'} alignItems={'center'} gap={1.5} sx={{minWidth: 0}}>
                        <Typography variant={'h6'} fontWeight={600} noWrap>
                            {t('Run details')}
                        </Typography>

                        <ExecutionStatusElement status={execution?.status}/>

                        {testRun && <ElementChip text={t('Test run')}/>}
                    </Stack>

                    <Tooltip title={t('Close')}>
                        <IconButton onClick={onClose} size={'small'} aria-label={t('Close')}>
                            <CloseIcon/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {!execution && executionQuery.isLoading && <DetailsLoading/>}

                {!execution && !executionQuery.isLoading && (
                    <Alert severity={'error'}>
                        {executionQuery.error
                            ? (executionQuery.error as Error).message
                            : t('Run not found')}
                    </Alert>
                )}

                {!!execution && (
                    <Stack gap={3} sx={{py: 1}}>
                        <Stack gap={1}>
                            <Typography variant={'h5'}>
                                {execution.flowName || flowName || t('Automation')}
                            </Typography>

                            <Stack direction={'row'} flexWrap={'wrap'} gap={3}>
                                {metaRow(t('Run ID'), execution.id)}
                                {metaRow(t('Part of run'), parentLink)}
                                {metaRow(
                                    t('Trigger entity'),
                                    execution.triggerEntityType
                                        ? `${triggerEntityLabel(execution.triggerEntityType, t)}${execution.triggerEntityId ? `: ${execution.triggerEntityId}` : ''}`
                                        : undefined
                                )}
                                {metaRow(t('Test recipient'), execution.testRecipient)}
                                {metaRow(t('Started at'), formatDate(execution.startedAt))}
                                {metaRow(t('Completed at'), formatDate(execution.completedAt))}
                                {metaRow(t('Duration'), formatDuration(execution.startedAt, execution.completedAt))}
                            </Stack>

                            {!!execution.errorMessage && (
                                <Alert severity={'error'} sx={{mt: 1}}>
                                    {execution.errorMessage}
                                </Alert>
                            )}
                        </Stack>

                        {expectedChildren > 0 && (
                            <>
                                <Divider/>

                                <Stack gap={1.5}>
                                    <Stack direction={'row'} alignItems={'baseline'} gap={1.5} flexWrap={'wrap'}>
                                        <Typography variant={'h6'}>
                                            {t('Member runs')}
                                        </Typography>

                                        {/* "3 of 349" beside the title: the cap is what gets mistaken for the audience. */}
                                        {!!scope?.limited && scope.total > scope.queued && (
                                            <Typography variant={'body2'} color={'text.secondary'}>
                                                {t('Member runs limited caption', {queued: scope.queued, total: scope.total})}
                                            </Typography>
                                        )}

                                        <Typography variant={'body2'} color={failedChildren ? 'error' : 'text.secondary'}>
                                            {childrenSummary}
                                        </Typography>
                                    </Stack>

                                    {!children.length && childrenQuery.isLoading && <DetailsLoading/>}

                                    {children.map(child => (
                                        <ChildRunRow key={child.id} execution={child} onOpen={onOpenExecution}/>
                                    ))}
                                </Stack>
                            </>
                        )}

                        {contextEntries.length > 0 && (
                            <>
                                <Divider/>

                                <Stack gap={1}>
                                    <Typography variant={'h6'}>
                                        {t('Run context')}
                                    </Typography>

                                    <JsonBlock value={JSON.stringify(execution.context, null, 2)}/>
                                </Stack>
                            </>
                        )}

                        <Divider/>

                        <Stack gap={1.5}>
                            <Typography variant={'h6'}>
                                {t('Steps in this run')}
                            </Typography>

                            {!steps.length && (
                                <Typography variant={'body2'} color={'text.secondary'}>
                                    {t('No steps recorded')}
                                </Typography>
                            )}

                            {steps.map((step, index) => {
                                const result = parseStepResult(step.result);
                                const report = step.nodeType === NodeType.SURVEY_REPORT ? surveyReportStepText(result, t) : undefined;
                                const teamEmail = step.nodeType === NodeType.SEND_TEAM_EMAIL ? teamEmailStepText(result, t) : undefined;

                                return (
                                    <Paper key={`${step.nodeId}-${index}`} variant={'outlined'} sx={{p: 2}}>
                                        <Stack gap={0.25}>
                                            <Typography variant={'subtitle2'} fontWeight={'bold'}>
                                                {step.nodeName || formatEnumValue(step.nodeType)}
                                            </Typography>

                                            <Typography variant={'body2'} color={'text.secondary'}>
                                                {t('Node ID')}: {step.nodeId}
                                            </Typography>

                                            <Typography variant={'body2'} color={'text.secondary'}>
                                                {formatDate(step.startedAt)}
                                                {' · '}
                                                {/* Trigger steps carry no completedAt: cap at the run's end, or a finished run's trigger keeps ticking. */}
                                                {formatDuration(step.startedAt, step.completedAt ?? execution.completedAt)}
                                            </Typography>
                                        </Stack>

                                        {step.nodeType === 'CONTINUE_IF' && !!step.result && (
                                            <Alert severity={result.evaluated ? 'success' : 'info'} sx={{mt: 1}}>
                                                {result.evaluated ? t('Continued') : t('Stopped here')}
                                                {/* `reason` is the API's English; a contact limit comes as `contactBlock` to word here. */}
                                                {' — '}{contactBlockText(result.contactBlock, t) ?? result.reason}
                                            </Alert>
                                        )}
                                        {/* Skip the wait on the test-run strip: the steps below ran early. */}
                                        {step.nodeType === 'WAIT' && result.skippedByTester === true && (
                                            <Alert severity={'info'} sx={{mt: 1}}>
                                                {t('Wait skipped in the test')}
                                            </Alert>
                                        )}
                                        {!!report && (
                                            <Alert severity={'info'} sx={{mt: 1}}>
                                                {report.text}
                                                {!!report.files.length && (
                                                    <Typography variant={'body2'} sx={{mt: 0.5}}>
                                                        {`${t('Files')}: ${report.files.join(', ')}`}
                                                    </Typography>
                                                )}
                                            </Alert>
                                        )}
                                        {!!teamEmail && (
                                            <Alert
                                                severity={teamEmail.outcome === 'sent' ? 'success' : teamEmail.outcome === 'refused' ? 'error' : 'warning'}
                                                sx={{mt: 1}}
                                            >
                                                {teamEmail.text}
                                                {!!teamEmail.files.length && (
                                                    <Typography variant={'body2'} sx={{mt: 0.5}}>
                                                        {`${t('Attachments')}: ${teamEmail.files.join(', ')}`}
                                                    </Typography>
                                                )}
                                            </Alert>
                                        )}
                                        {!!step.error && (
                                            <Alert severity={'error'} sx={{mt: 1}}>
                                                {step.error}
                                            </Alert>
                                        )}

                                        {!!step.result && (
                                            <Stack gap={0.5} sx={{mt: 1}}>
                                                <Typography variant={'body2'} color={'text.secondary'}>
                                                    {t('Result')}
                                                </Typography>

                                                <JsonBlock value={formatStepResult(step.result)} maxHeight={200}/>
                                            </Stack>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Stack>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ExecutionDetailsDialog;
